// Message thread for operator Slack-lite

import React, { useEffect, useRef } from 'react';
import {
    formatOperatorChatFileBytes,
    formatOperatorMessageTime,
} from '../../services/operatorChatService';

function AttachmentList({ attachments }) {
    if (!attachments?.length) return null;
    return (
        <ul className="op-msg-attachments">
            {attachments.map((att) => (
                <li key={att.itemId || att.storagePath || att.fileName}>
                    <a
                        className="op-msg-attachment"
                        href={att.downloadUrl || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={att.fileName}
                    >
                        <span className="op-msg-attachment__name">{att.fileName || 'File'}</span>
                        <span className="op-msg-attachment__meta">
                            {att.source === 'drive' ? 'Smoke Drive · ' : ''}
                            {formatOperatorChatFileBytes(att.sizeBytes)}
                            {att.contentType ? ` · ${att.contentType}` : ''}
                        </span>
                    </a>
                </li>
            ))}
        </ul>
    );
}

export default function OperatorChatThread({
    messages,
    loading,
    currentUid,
    onLoadOlder,
    loadingOlder,
    hasMoreOlder,
    onPin,
    onUnpin,
    highlightMessageId,
}) {
    const bottomRef = useRef(null);
    const scrollerRef = useRef(null);
    const prevLenRef = useRef(0);

    useEffect(() => {
        if (!messages?.length) return;
        if (messages.length > prevLenRef.current) {
            const grewAtEnd =
                prevLenRef.current === 0 ||
                messages[messages.length - 1]?.fromUid === currentUid ||
                messages.length - prevLenRef.current === 1;
            if (grewAtEnd) {
                bottomRef.current?.scrollIntoView({
                    behavior: prevLenRef.current === 0 ? 'auto' : 'smooth',
                });
            }
        }
        prevLenRef.current = messages.length;
    }, [messages, currentUid]);

    useEffect(() => {
        if (!highlightMessageId) return;
        const el = scrollerRef.current?.querySelector(`[data-msg-id="${highlightMessageId}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [highlightMessageId, messages]);

    if (loading && (!messages || messages.length === 0)) {
        return <div className="op-msg-thread op-msg-empty">Loading messages…</div>;
    }

    if (!messages || messages.length === 0) {
        return (
            <div className="op-msg-thread op-msg-empty">
                No messages yet. Say hello or attach a file.
            </div>
        );
    }

    return (
        <div className="op-msg-thread" ref={scrollerRef}>
            {hasMoreOlder ? (
                <div className="op-msg-thread__older">
                    <button
                        type="button"
                        className="op-msg-btn op-msg-btn--ghost op-msg-btn--compact"
                        disabled={loadingOlder}
                        onClick={onLoadOlder}
                    >
                        {loadingOlder ? 'Loading…' : 'Load older'}
                    </button>
                </div>
            ) : null}

            <ul className="op-msg-thread__list">
                {messages.map((msg) => {
                    const mine = msg.fromUid === currentUid;
                    return (
                        <li
                            key={msg.id}
                            data-msg-id={msg.id}
                            className={`op-msg-bubble-row${mine ? ' is-mine' : ''}${
                                highlightMessageId === msg.id ? ' is-highlight' : ''
                            }`}
                        >
                            <div className="op-msg-bubble lsc-glass-surface">
                                <div className="op-msg-bubble__meta">
                                    <span className="op-msg-bubble__who">{msg.fromName || 'Operator'}</span>
                                    <span className="op-msg-bubble__time">
                                        {formatOperatorMessageTime(msg.createdAt)}
                                    </span>
                                </div>
                                {msg.bodyHtml ? (
                                    <div
                                        className="op-msg-bubble__body"
                                        dangerouslySetInnerHTML={{ __html: msg.bodyHtml }}
                                    />
                                ) : msg.bodyText ? (
                                    <p className="op-msg-bubble__body">{msg.bodyText}</p>
                                ) : null}
                                <AttachmentList attachments={msg.attachments} />
                                <div className="op-msg-bubble__actions">
                                    {msg.pinned ? (
                                        <button
                                            type="button"
                                            className="op-msg-link-btn"
                                            onClick={() => onUnpin?.(msg.id)}
                                        >
                                            Unpin
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="op-msg-link-btn"
                                            onClick={() => onPin?.(msg.id)}
                                        >
                                            Pin
                                        </button>
                                    )}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
            <div ref={bottomRef} />
        </div>
    );
}
